import React from 'react'

import { twMerge } from 'tailwind-merge'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { Tag } from '@/components/tags/Tag'

import { compareTagsByPosition } from '@/helpers/tags'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useTags } from '@/hooks/tags/useTags'

import { UUID } from '@/types/Id'

interface TagListProps {
  tagIds: UUID[]
  maxTagsToShow?: number
  className?: string
  tagClassName?: string
}

export const TagList: React.FC<TagListProps> = ({
  tagIds,
  maxTagsToShow,
  className,
  tagClassName,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tags } = useTags({ orgId: activeOrgId, tagIds, tagType: null })
  if (!tags) {
    return null
  }
  const cutoff = maxTagsToShow ?? tagIds.length
  const sortedTags = [...tags].sort(compareTagsByPosition)
  return (
    <div className={twMerge('flex items-center gap-2', className)}>
      {sortedTags.slice(0, cutoff).map((tag) => (
        <Tag key={tag.id} tagId={tag.id} className={tagClassName} />
      ))}
      {sortedTags.length > cutoff && (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild={true}>
            <div className="flex items-center justify-center h-[22px] rounded-full border border-primary-200 dark:border-primary-800 whitespace-nowrap px-2 py-1">
              <span className="text-[13px] font-medium text-primary-500 dark:text-primary-400">
                +{sortedTags.length - cutoff} more
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent
            inverseColors={true}
            className="w-[200px] py-2 px-[10px]"
          >
            <div className="flex flex-col items-start w-full gap-2">
              {sortedTags.slice(maxTagsToShow).map((tag) => (
                <Tag key={tag.id} tagId={tag.id} className={tagClassName} />
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

TagList.displayName = 'TagList'
